<script lang="ts">
  // One row in a Section: label + description on the left, a control on the
  // right. Rows are separated by hairlines.
  //   <ListRow label="Donkere modus" description="…" for="dark"><Toggle id="dark" … /></ListRow>
  //   <ListRow label="Abonnement" href="/app/instellingen/account" />      navigable row with chevron
  //   <ListRow label="Notitie" stacked>…full-width control below…</ListRow>
  // `wide` moves the control under the text on small screens, `stacked` always.
  // `highlighted` flashes the row (search result), `below` renders full width.
  import type { Snippet } from 'svelte';
  import { ChevronRight } from '@lucide/svelte';

  interface Props {
    label: string;
    description?: string;
    /** id of the control, so the label is connected to it. */
    for?: string;
    /** DOM id of the row (scroll target). */
    id?: string;
    wide?: boolean;
    stacked?: boolean;
    disabled?: boolean;
    highlighted?: boolean;
    tone?: 'default' | 'danger';
    /** Makes the whole row a link with a chevron. */
    href?: string;
    leading?: Snippet;
    badge?: Snippet;
    children?: Snippet;
    below?: Snippet;
    class?: string;
  }
  let { label, description, for: htmlFor, id, wide = false, stacked = false, disabled = false, highlighted = false, tone = 'default', href, leading, badge, children, below, class: cls = '' }: Props = $props();
</script>

{#snippet text()}
  {#if leading}<div class="leading">{@render leading()}</div>{/if}
  <div class="text">
    <div class="label-line">
      {#if htmlFor && !href}<label class="label" for={htmlFor}>{label}</label>{:else}<span class="label">{label}</span>{/if}
      {#if badge}{@render badge()}{/if}
    </div>
    {#if description}<p class="desc">{description}</p>{/if}
  </div>
{/snippet}

{#if href}
  <a class="list-row link {cls}" class:disabled class:flash={highlighted} class:danger={tone === 'danger'} {id} {href} aria-disabled={disabled || undefined}>
    <div class="main">{@render text()}</div>
    <div class="control">
      {#if children}{@render children()}{/if}
      <ChevronRight class="chev" aria-hidden="true" />
    </div>
  </a>
{:else}
  <div class="list-row {cls}" class:wide class:stacked class:disabled class:flash={highlighted} class:danger={tone === 'danger'} {id}>
    <div class="main">{@render text()}</div>
    {#if children}<div class="control">{@render children()}</div>{/if}
    {#if below}<div class="below">{@render below()}</div>{/if}
  </div>
{/if}

<style>
  .list-row {
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto;
    align-items: center;
    column-gap: var(--space-6);
    row-gap: var(--space-3);
    padding: var(--space-4) var(--card-pad);
    border-top: 1px solid var(--border);
    transition: background 600ms var(--ease);
    scroll-margin-top: 96px;
    color: var(--text);
  }
  .list-row:first-child { border-top: 0; }
  .stacked { grid-template-columns: minmax(0, 1fr); }
  .flash { background: var(--brand-softer); box-shadow: inset 3px 0 0 var(--brand); }
  .disabled .main, .disabled .control { opacity: 0.5; pointer-events: none; }
  .main { display: flex; align-items: center; gap: var(--space-3); min-width: 0; }
  .leading { display: flex; flex-shrink: 0; color: var(--text-2); }
  .text { min-width: 0; display: flex; flex-direction: column; gap: var(--space-0_5); }
  .label-line { display: flex; align-items: center; gap: var(--space-2); flex-wrap: wrap; }
  .label { font-size: var(--text-base); font-weight: var(--weight-semibold); color: var(--text); }
  .danger .label { color: var(--danger); }
  .desc { font-size: var(--text-sm); color: var(--text-2); line-height: var(--leading-normal); max-width: 60ch; }
  .control { display: flex; align-items: center; justify-content: flex-end; gap: var(--space-2); min-width: 0; }
  .stacked .control { justify-content: flex-start; }
  .below { grid-column: 1 / -1; min-width: 0; }

  .link { text-decoration: none; transition: background var(--dur); }
  .link:hover { background: var(--surface-hover); text-decoration: none; }
  .link:focus-visible { outline: none; box-shadow: inset var(--ring); }
  .link :global(.chev) { width: var(--icon-md); height: var(--icon-md); color: var(--text-3); }

  @media (max-width: 640px) {
    .list-row { column-gap: var(--space-4); }
    .wide { grid-template-columns: minmax(0, 1fr); }
    .wide .control { justify-content: flex-start; }
  }
</style>
